/**
 * How a node looks, in one place: its icon, its app logo, which of the two
 * tile styles it wears, and the one-line summary printed under its name.
 *
 * Nothing here is a colour. A node is a neutral tile with its icon; only a
 * trigger is tinted (the brand soft tile), because it is the one thing that
 * starts a run. Category is carried by the icon, never by a hue.
 *
 * Pure and framework-free apart from the icon components themselves, so it
 * can be tested without rendering anything.
 */

import { Box } from 'lucide-react';
import { NODE_CATALOG } from 'features/agents/WorkflowEditor/nodeCatalog';
import { appLogoForSpec, LOGO_NEEDS_ADAPTIVE } from 'features/agents/WorkflowEditor/appLogos';
import { parseCron, describeSchedule } from 'features/agents/WorkflowEditor/scheduleCron';

/** The lucide icon for a kind (a generic box for anything unknown). */
export function iconFor(kind) {
  return NODE_CATALOG[kind]?.icon || Box;
}

/** The connected app's (or Lucid's) own mark for a kind, else null. */
export function logoFor(kind) {
  return appLogoForSpec(NODE_CATALOG[kind]);
}

/** Mono dark-on-transparent logos need inverting in dark mode. */
export function logoNeedsAdaptive(logo) {
  return !!logo && LOGO_NEEDS_ADAPTIVE.has(logo);
}

/** 'trigger' for anything that starts a run, 'neutral' for everything else. */
export function accentFor(kind) {
  return NODE_CATALOG[kind]?.category === 'trigger' ? 'trigger' : 'neutral';
}

/** Tile background for an accent. Only a trigger is tinted. */
export function accentTint(accent) {
  return accent === 'trigger' ? 'var(--brand-primary-soft)' : 'var(--surface-sunken)';
}

/** Tile border for an accent. */
export function accentEdge(accent) {
  return accent === 'trigger'
    ? 'color-mix(in srgb, var(--brand-primary) 28%, transparent)'
    : 'var(--border-subtle)';
}

/** Icon colour for an accent. */
export function accentIconColor(accent) {
  return accent === 'trigger' ? 'var(--brand-primary)' : 'var(--text-secondary)';
}

/**
 * What the palette sections used to colour. Kept so existing importers keep
 * working; every section is neutral except the one that starts a run.
 */
export const SECTION_ACCENT = {
  triggers: 'trigger', ai: 'neutral', actions: 'neutral', data: 'neutral', flow: 'neutral',
};

/* ── summaries ───────────────────────────────────────────────────────────── */

const MAX = 80;

const clean = (v) => String(v ?? '').replace(/\s+/g, ' ').trim();
const cap = (s) => (s.length > MAX ? `${s.slice(0, MAX - 1)}…` : s);
const present = (v) => v !== undefined && v !== null && clean(v) !== '';
/** `{{ $input.value > 0 }}` -> `$input.value > 0` */
const bare = (v) => clean(v).replace(/^\{\{\s*/, '').replace(/\s*\}\}$/, '');
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
// A Slack id (C0123ABCD) tells a reader nothing; a name does.
const SLACK_ID = /^[CGDUW][A-Z0-9]{7,}$/;

function hostOf(url) {
  const raw = clean(url);
  if (!raw) return '';
  if (raw.includes('{{')) return raw;
  try {
    return new URL(/^[a-z]+:\/\//i.test(raw) ? raw : `https://${raw}`).host || raw;
  } catch (_) {
    return raw.replace(/^[a-z]+:\/\//i, '').split('/')[0];
  }
}

function firstSqlLine(sql) {
  for (const line of String(sql ?? '').split('\n')) {
    const t = line.trim();
    if (t && !t.startsWith('--')) return t;
  }
  return '';
}

function scheduleText(c) {
  if (!present(c.cron)) return '';
  try {
    const text = describeSchedule(parseCron(c.cron), c.timezone);
    return text.replace(/^Runs /, '').replace(/\s*\([^)]*\)\s*$/, '');
  } catch (_) {
    return clean(c.cron);
  }
}

const ASK_TASK = {
  answer: (c) => (present(c.question) ? clean(c.question) : 'Answer a question'),
  investigate_issue: () => 'Investigate the alert',
  improve_eval: () => 'Improve an eval',
};

const CODING_AGENT = { auto: 'Connected agent', claudecode: 'Claude Code', cursor: 'Cursor' };

function integrationSummary(kind, c) {
  const provider = kind.split('_')[0];
  if (provider === 'slack') {
    const ch = clean(c.channel);
    if (ch && !SLACK_ID.test(ch)) return ch.startsWith('#') ? ch : `#${ch}`;
    if (present(c.text)) return clean(c.text);
    if (present(c.query)) return clean(c.query);
    return '';
  }
  if (provider === 'github') {
    const repo = clean(c.repo);
    if (!repo) return '';
    const n = c.issue_number ?? c.pull_number;
    return present(n) ? `${repo}#${clean(n)}` : repo;
  }
  if (provider === 'linear') {
    if (present(c.title)) return clean(c.title);
    if (present(c.query)) return clean(c.query);
    return '';
  }
  return '';
}

/**
 * One line that says what a configured node will do, or '' when there is
 * nothing worth saying yet. Never throws: a half-filled config is normal.
 *
 * @param {string} kind
 * @param {object} [config]
 * @returns {string}
 */
export function summaryFor(kind, config) {
  const c = config && typeof config === 'object' ? config : {};
  let out = '';
  switch (kind) {
    case 'trigger_schedule': out = scheduleText(c); break;
    case 'trigger_webhook': out = c.require_hmac ? 'Signed webhook' : 'Webhook URL'; break;
    case 'trigger_alert': {
      const n = Array.isArray(c.alert_ids) ? c.alert_ids.length : (present(c.alert_id) ? 1 : 0);
      out = n ? plural(n, 'alert', 'alerts') : '';
      break;
    }
    case 'ai_agent':
    case 'llm_call': out = clean(c.model?.model); break;
    case 'trodo_ask': out = (ASK_TASK[c.task] || ASK_TASK.answer)(c); break;
    case 'hand_to_coding_agent': out = CODING_AGENT[c.agent] || ''; break;
    case 'data_query': {
      const win = c.time_window?.kind === 'relative' && present(c.time_window.value) ? ` · last ${clean(c.time_window.value)}` : '';
      out = present(c.entity) ? `${clean(c.entity)}${win}` : '';
      break;
    }
    case 'data_sql': out = firstSqlLine(c.sql); break;
    case 'http_request': {
      const host = hostOf(c.url);
      out = host ? `${clean(c.method || 'GET').toUpperCase()} ${host}` : '';
      break;
    }
    case 'set_fields': {
      const n = Array.isArray(c.assignments) ? c.assignments.length : 0;
      out = n ? plural(n, 'field', 'fields') : '';
      break;
    }
    case 'filter_node':
    case 'if_node': out = bare(c.expression); break;
    case 'decide': {
      const n = c.questions && typeof c.questions === 'object' ? Object.keys(c.questions).length : 0;
      out = n ? plural(n, 'question', 'questions') : '';
      break;
    }
    case 'loop_node': out = bare(c.items_expression); break;
    case 'wait_node': out = present(c.seconds) ? `${clean(c.seconds)}s` : ''; break;
    case 'stop_node': out = c.status === 'failed' ? 'Ends as failed' : 'Ends as succeeded'; break;
    default:
      out = NODE_CATALOG[kind]?.category === 'integration' ? integrationSummary(kind, c) : '';
  }
  return cap(out);
}
